/**
 * EngineStatusBanner: the graceful-degrade spine of the workbench.
 *
 * Two independent signals, each a DS `Alert` above the page:
 *
 * 1. Configuration, from `GET /api/status` (via `useEngineStatus`), which always
 *    answers 200 and reports whether the I2Q engine's runtime sources are wired.
 *    When the engine is not fully configured the live pipeline endpoints 503 and
 *    the banner says so. Production never shows sample data; only the local dev
 *    server (`import.meta.env.DEV`) falls back to fixtures, and only there does the
 *    banner say "showing sample data".
 *
 * 2. Data health, from `GET /api/status/data` (via `useDataHealth`): whether the
 *    mail import and the master-data sync are current. A danger issue (import
 *    stalled, database read-only, store unreachable) renders a danger alert, a
 *    warning issue (master data older than its SLA) a warning alert. The server
 *    writes the sentences; they are printed verbatim. Info issues (a backlog) are
 *    not shown here. When the health read itself fails, a warning says that the
 *    freshness of the data could not be checked (end user review F10, 11 Oct 2026):
 *    silence would read as "all fine".
 *
 * Neither alert blocks the UI or is dismissible: they reflect live state, so they
 * come and go with that state.
 */

import { AlertTriangle, Icon } from "@norican/icons";
import { Alert } from "@norican/react";

import { type DataHealthIssue, useDataHealth, useEngineStatus } from "@/lib/api";

const DEV_FIXTURES = import.meta.env.DEV;

/** Which specific sources are missing, for the banner's supporting line. */
function missingSources(db: boolean, foundry: boolean): string {
  const missing: string[] = [];
  if (!db) {
    missing.push("catalogue mirror (database)");
  }
  if (!foundry) {
    missing.push("Foundry models");
  }
  return missing.join(" and ");
}

/** The "engine not configured" alert, or null when the engine is configured. */
function ConfigurationAlert() {
  const { data, isLoading, isError } = useEngineStatus();

  // Don't flash a banner during the first probe. `/api/status` never 503s, so the
  // only failure here is a transport error, which is treated as "not configured" too.
  if (isLoading) {
    return null;
  }

  const configured = !isError && (data?.configured ?? false);
  if (configured) {
    return null;
  }

  const detail =
    !isError && data
      ? missingSources(data.db_configured, data.foundry_configured)
      : "I2Q did not respond";
  const verb = detail.includes(" and ") ? "are" : "is";

  if (DEV_FIXTURES) {
    return (
      <Alert
        tone="warning"
        title="I2Q not configured, showing sample data (local dev)"
        icon={<Icon icon={AlertTriangle} />}
      >
        The local dev server shows sample fixtures because {detail} {verb} not available here. A
        deployed build never shows sample data.
      </Alert>
    );
  }

  return (
    <Alert
      tone="warning"
      title="I2Q not configured, live data not available"
      icon={<Icon icon={AlertTriangle} />}
    >
      I2Q cannot load live cases, draft lines and figures because {detail} {verb} not available
      here. The pages stay empty until IT configures I2Q; there is no sample data.
    </Alert>
  );
}

/** Shown when `GET /api/status/data` itself fails. */
export const HEALTH_UNKNOWN_TEXT =
  "I2Q could not check whether the mail import and the master data are up to date. What you see may be older than usual. I2Q checks again in a moment.";

/** The data-health alert, or null when nothing is wrong (or only info issues exist). */
export function DataHealthAlert() {
  const { data, isError } = useDataHealth();
  if (isError) {
    return (
      <Alert
        tone="warning"
        title="I2Q could not check the data freshness"
        icon={<Icon icon={AlertTriangle} />}
        data-health-unknown
      >
        {HEALTH_UNKNOWN_TEXT}
      </Alert>
    );
  }
  const issues: DataHealthIssue[] = (data?.issues ?? []).filter(
    (issue) => issue.severity === "danger" || issue.severity === "warning",
  );
  if (issues.length === 0) {
    return null;
  }
  const danger = issues.some((issue) => issue.severity === "danger");
  // Danger sentences first, so the most urgent one leads.
  const ordered = [
    ...issues.filter((issue) => issue.severity === "danger"),
    ...issues.filter((issue) => issue.severity !== "danger"),
  ];
  const first = ordered[0];

  return (
    <Alert
      tone={danger ? "danger" : "warning"}
      title={danger ? "The data shown here is not up to date" : "Some data may be out of date"}
      icon={<Icon icon={AlertTriangle} />}
    >
      {ordered.length === 1 && first ? (
        first.message
      ) : (
        <ul className="flex list-disc flex-col gap-0.5 pl-4">
          {ordered.map((issue) => (
            <li key={`${issue.code}-${issue.message}`}>{issue.message}</li>
          ))}
        </ul>
      )}
    </Alert>
  );
}

export function EngineStatusBanner() {
  return (
    <>
      <ConfigurationAlert />
      <DataHealthAlert />
    </>
  );
}
